import { Component, AfterViewInit, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import AOS from 'aos';
import { RelevantTechgeekComponent } from '../relevant-techgeek/relevant-techgeek.component';
import { ResourcesService } from '../../../services/resources.service';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';

interface ArticlePost {
  id: number;
  title: string;
  image: string;
  slug?: string;
}
@Component({
  selector: 'app-techgeek-4',
  imports: [CommonModule, RelevantTechgeekComponent, ReactiveFormsModule, HttpClientModule],
  standalone: true,
  providers: [ResourcesService],
  templateUrl: './techgeek-4.component.html',
  styleUrl: './techgeek-4.component.scss'
})
export class Techgeek4Component implements AfterViewInit, OnInit, OnDestroy {
  isModalOpen = false;
  isSubmitting = false;
  submitSuccess = false;
  errorMessage = '';
  downloadForm!: FormGroup;
  private jsonLdScript: any;

  readonly techGeekTitle = "Tech Geek Ed. 4";
  constructor(
    private fb: FormBuilder,
    private resourcesService: ResourcesService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) {
    this.downloadForm = this.fb.group({
      name: ['', [Validators.required, Validators.pattern('^[A-Za-z]+$')]], // Added pattern for consistency with apply-position
      email: ['', [Validators.required, Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{3,4}$')]]
    });
  }

  openModal() {
    this.isModalOpen = true;
    this.submitSuccess = false;
    this.errorMessage = '';
    this.isSubmitting = false;
    this.downloadForm.reset();
  }

  closeModal() {
    this.isModalOpen = false;
  }

  onSubmit() {
    if (this.downloadForm.valid) {

      this.isSubmitting = true;
      this.errorMessage = '';

      const payload = {
        Name: this.downloadForm.value.name,
        Email: this.downloadForm.value.email,
        TechGeekTitle: this.techGeekTitle
      };

      this.resourcesService.sendTechGeekEmail(payload).subscribe({
        next: (response: any) => {
          if (response && !response.IsError) {
            this.submitSuccess = true;
            this.isSubmitting = false;

            setTimeout(() => {
              this.closeModal();
            }, 4000);
          } else {
            this.isSubmitting = false;
            this.errorMessage = response?.Message;
          }
        },
        error: () => {
          this.isSubmitting = false;
          this.errorMessage = "Server error. Please try again.";
        }
      });
    }
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
  }

  ngAfterViewInit() {
    AOS.init();
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }

injectSchemaMarkup(): void {
    const techGeek4Schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-4",
          "url": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-4",
          "name": "Tech Geek Ed. 4 — Kalpita Technologies Tech Magazine",
          "description": "Kalpita Tech Geek Magazine Edition 4 covers Apple Vision Pro spatial computing, payment rings, m-commerce trends, Withings BeamO, NVIDIA Jetson, and AI health innovations.",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [
              ".softwaredev-heading",
              ".takeaways-section .takeaway-item p",
              ".section-title"
            ]
          },
          "significantLink": ["https://heyzine.com/flip-book/b2ea28d3be.html"],
          "datePublished": "2024-01-01",
          "dateModified": "2024-03-31",
          "isPartOf": { "@type": "WebSite", "@id": "https://www.kalpitatechnologies.com/#website" }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-4#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Tech Geek", "item": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek" },
            { "@type": "ListItem", "position": 3, "name": "Tech Geek Ed. 4", "item": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-4" }
          ]
        },
        {
          "@type": "TechArticle",
          "headline": "Tech Geek Ed. 4: Spatial Computing, AI Hardware, and Smart Health",
          "articleBody": "Kalpita Tech Geek Magazine Edition 4 explores spatial computing (Apple Vision Pro), smart health (Withings BeamO, Owlet), compact AI supercomputing (NVIDIA DIGITS), and comparisons of leading AI tools like Claude vs ChatGPT-4.",
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "associatedMedia": {
            "@type": "MediaObject",
            "contentUrl": "https://heyzine.com/flip-book/b2ea28d3be.html",
            "name": "Tech Geek Ed. 4 — Digital Flipbook Edition"
          }
        },
        {
          "@type": "HowTo",
          "name": "How to Leverage Technology Trends from Tech Geek Ed. 4",
          "step": [
            { "@type": "HowToStep", "name": "Explore Spatial Computing", "text": "Use Apple Vision Pro's 3D camera and Eyesight technology to experience mixed reality workflows." },
            { "@type": "HowToStep", "name": "Adopt Wearable Payments", "text": "Evaluate payment rings like RingPay or Pay Ring for secure, chic contactless transactions." },
            { "@type": "HowToStep", "name": "Implement Smart Health", "text": "Deploy devices like Withings BeamO for 4-in-1 home health tracking (ECG, Oximeter, etc.)." },
            { "@type": "HowToStep", "name": "Evaluate AI Hardware", "text": "Compare NVIDIA Jetson Orin Nano ($249) and Project DIGITS ($3,000) for edge and research AI needs." },
            { "@type": "HowToStep", "name": "Choose AI Coding Tools", "text": "Compare GitHub Copilot's IDE integration versus ChatGPT's conversational problem-solving." }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is Withings BeamO?",
              "acceptedAnswer": { "@type": "Answer", "text": "Withings BeamO is a 4-in-1 health assessment device that combines an ECG, oximeter, stethoscope, and infrared thermometer for home use." }
            },
            {
              "@type": "Question",
              "name": "How does AI detect Tuberculosis in India?",
              "acceptedAnswer": { "@type": "Answer", "text": "Apps by Wadhwani AI analyze cough sounds via computer vision algorithms, detecting 14% more cases than traditional methods." }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://www.kalpitatechnologies.com/#localbusiness",
          "name": "Kalpita Technologies — Austin Office",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
            "addressLocality": "Austin",
            "addressRegion": "TX",
            "postalCode": "78746",
            "addressCountry": "US"
          },
          "telephone": "+1-512-240-2484",
          "url": "https://www.kalpitatechnologies.com/"
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(techGeek4Schema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  // articles: ArticlePost[] = [
  //   {
  //     id: 1,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-one.webp'
  //   },
  //   {
  //     id: 2,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-two.webp'
  //   },
  //   {
  //     id: 3,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-three.webp'
  //   },
  //   {
  //     id: 4,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-four.webp'
  //   },
  //   {
  //     id: 5,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-five.webp'
  //   },
  //   {
  //     id: 6,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-six.webp'
  //   },
  //   {
  //     id: 7,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-seven.webp'
  //   },
  //   {
  //     id: 8,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-eight.webp'
  //   }
  // ];
  onArticleClick(article: ArticlePost): void {
    console.log('Article clicked:', article);
    // Handle navigation to article detail page
  }

  trackByArticle(index: number, article: ArticlePost): number {
    return article.id;
  }
}
